import { useState } from 'react';
import { getLocalTimeZone, today } from '@internationalized/date';
import { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import { parseISO } from 'date-fns';

import { ClientContactMethod } from '@/api';
import ContactDetails from '@/components/ContactDetails';
import Box from '@/design_system/Box';
import Button from '@/design_system/Button';
import Dialog from '@/design_system/Dialog';
import { Footer, FooterButtons, FooterError } from '@/design_system/Footer';
import InputDate from '@/design_system/InputDate';
import { InputSelect } from '@/design_system/InputSelect/InputSelect';
import { Label } from '@/design_system/Label/Label';
import { OptionalLabel } from '@/design_system/Label/OptionalLabel';
import { RadioItem, RadioItemGroup } from '@/design_system/Radio/RadioItem/RadioItem';
import Stack from '@/design_system/Stack';
import TextArea from '@/design_system/TextArea';
import Paragraph from '@/design_system/Typography/Paragraph';
import IconDownload from '@/icons/Download.svg';
import { useRecordClientContact } from '@/models/article';
import { useRequestDocuments } from '@/models/request';
import { useArticleContext } from '@/routes/Requests/contexts/ArticleContext';
import { useRequestContext } from '@/routes/Requests/contexts/RequestContext';
import useViewPort from '@/utils/useViewport';

export const RecordClientContactDialog = ({
  isOpen,
  setIsOpen,
  onSuccess,
}: {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  onSuccess?: () => void;
}) => {
  const { t } = useLingui();
  const { article } = useArticleContext();

  return (
    <Dialog
      title={
        article.taskType === 'contact_client'
          ? t({
              id: 'article.actions.contact-client.dialog.title.task',
              message: 'Record client outreach',
            })
          : t({
              id: 'article.actions.contact-client.dialog.title',
              message: 'Record client outreach',
            })
      }
      isOpen={isOpen}
      onOpenChange={setIsOpen}
    >
      <RecordClientContactDialogContent
        onSuccess={() => {
          setIsOpen(false);
          onSuccess?.();
        }}
      />
    </Dialog>
  );
};

const RecordClientContactDialogContent = ({ onSuccess }: { onSuccess: () => void }) => {
  const { t, i18n } = useLingui();
  const { request, workflow } = useRequestContext();
  const { article } = useArticleContext();
  const { isMobile } = useViewPort();

  const todayDate = today(getLocalTimeZone());

  const [contactedAt, setContactedAt] = useState<string | null>(null);
  const [contactMethod, setContactMethod] = useState<ClientContactMethod | null>(null);
  const [salesAdvisor, setSalesAdvisor] = useState<string | null>(null);
  const [additionalInformation, setAdditionalInformation] = useState<string>('');
  const [showErrors, setShowErrors] = useState(false);
  const [apiError, setApiError] = useState('');

  const { mutateAsync: recordClientContact, isPending: isRecordClientContactPending } =
    useRecordClientContact({ articleId: article.id });

  if (!request.client) {
    return null;
  }

  const salesAdvisorOptions = request.store?.storeConfig?.salesAdvisors?.map((salesAdvisor) => ({
    id: salesAdvisor,
  }));

  const save = async () => {
    setShowErrors(true);

    if (
      !contactedAt ||
      parseISO(contactedAt) > new Date() ||
      !contactMethod ||
      (salesAdvisorOptions?.length && !salesAdvisor)
    ) {
      return;
    }

    try {
      await recordClientContact({
        contactedAt,
        contactMethod,
        salesAdvisor: salesAdvisor ?? undefined,
        additionalInformation,
      });

      onSuccess();
    } catch (error: any) {
      console.error(error);
      setApiError(
        (error.message as string) ?? t({ id: '_general.error.unknown', message: 'Unknown error' })
      );
    }
  };

  const showContactDetails =
    !!request.client.name || !!request.client.email || !!request.client.phone;
  const showOrganizationQuoteButton =
    !!workflow?.config.organizationQuote?.enabled && article.step?.step === 'validation';

  return (
    <>
      <main>
        <Stack gap="1rem">
          <Stack gap="0.5rem">
            <Paragraph>
              <Trans id="article.actions.contact-client.dialog.description">
                Provide the details of your exchange with the client.
              </Trans>{' '}
              {article.step?.step === 'validation' &&
                article.step.config.actOnBehalfOfClient &&
                article.taskType === 'contact_client' && (
                  <Trans id="article.actions.contact-client.dialog.description.validation">
                    Once saved, you'll be able to validate or refuse the quote.
                  </Trans>
                )}
            </Paragraph>

            {showContactDetails && (
              <Box padding="12px">
                <ContactDetails
                  type="client"
                  name={request.client.name}
                  email={request.client.email}
                  phone={request.client.phone}
                  size="medium"
                />
              </Box>
            )}
          </Stack>

          <InputDate
            label={t({
              id: 'article.actions.contact-client.dialog.contact-date',
              message: 'Date of contact',
            })}
            value={contactedAt}
            onChange={(newContactedAt) => setContactedAt(newContactedAt ?? null)}
            maxValue={todayDate}
            error={
              showErrors
                ? !contactedAt
                  ? t({
                      id: 'article.actions.contact-client.dialog.errors.missing-contact-date',
                      message: 'Please add a date of contact',
                    })
                  : parseISO(contactedAt) > new Date()
                    ? t({
                        id: 'article.actions.contact-client.dialog.errors.contact-date-in-future',
                        message: 'The date of contact must be in the past',
                      })
                    : undefined
                : undefined
            }
          />

          <Stack gap="0.5rem">
            <Label
              label={t({
                id: 'article.actions.contact-client.dialog.contact-method',
                message: 'Method of contact',
              })}
            >
              <RadioItemGroup
                direction={isMobile ? 'column' : 'row'}
                name="contact-method"
                value={contactMethod}
                onChange={setContactMethod}
                isInvalid={showErrors ? !contactMethod : false}
              >
                {CONTACT_METHOD_OPTIONS.map((contactMethod) => (
                  <RadioItem
                    key={contactMethod.id}
                    value={contactMethod.id}
                    ariaLabel={i18n._(contactMethod.label)}
                  >
                    {i18n._(contactMethod.label)}
                  </RadioItem>
                ))}
              </RadioItemGroup>
            </Label>
          </Stack>

          {!!salesAdvisorOptions?.length && (
            <InputSelect
              variant="select"
              label={t({
                id: 'article.actions.contact-client.dialog.sales-advisor.label',
                message: 'Sales advisor',
              })}
              value={salesAdvisorOptions.find((option) => option.id === salesAdvisor)}
              options={salesAdvisorOptions}
              placeholder={t({
                id: 'article.actions.contact-client.dialog.sales-advisor.placeholder',
                message: 'Select an option',
              })}
              onChange={(newSalesAdvisor) => {
                setSalesAdvisor(newSalesAdvisor?.id ?? null);
              }}
              getOptionValue={(option) => option.id}
              getOptionLabel={(option) => option.id}
              isClearable
              error={
                showErrors && !salesAdvisor
                  ? t({
                      id: 'article.actions.contact-client.dialog.errors.missing-sales-advisor',
                      message: 'Please select a sales advisor',
                    })
                  : undefined
              }
            />
          )}

          <TextArea
            label={
              <>
                <Trans id="article.actions.contact-client.dialog.additional-info.label">
                  Additional information
                </Trans>{' '}
                <OptionalLabel />
              </>
            }
            placeholder={t({
              id: 'article.actions.contact-client.dialog.additional-info.placeholder',
              message: 'Add any relevant details...',
            })}
            value={additionalInformation}
            onChange={(e) => setAdditionalInformation(e.target.value)}
            rows={4}
          />
        </Stack>
      </main>
      <Footer>
        {!!apiError && <FooterError>{apiError}</FooterError>}
        <FooterButtons>
          {showOrganizationQuoteButton && <DownloadOrganizationQuoteButton />}
          <Button
            variant="primary"
            size="medium"
            onPress={save}
            isLoading={isRecordClientContactPending}
          >
            <Trans id="article.actions.contact-client.dialog.save">Confirm outreach</Trans>
          </Button>
        </FooterButtons>
      </Footer>
    </>
  );
};

export const CONTACT_METHOD_OPTIONS = [
  {
    id: 'whatsapp-sms',
    label: msg({
      id: 'article.actions.contact-client.dialog.contact-methods.whatsapp-sms',
      message: 'WhatsApp / SMS',
    }),
  },
  {
    id: 'email',
    label: msg({
      id: 'article.actions.contact-client.dialog.contact-methods.email',
      message: 'Email',
    }),
  },
  {
    id: 'other',
    label: msg({
      id: 'article.actions.contact-client.dialog.contact-methods.other',
      message: 'Other',
    }),
  },
] satisfies { id: ClientContactMethod; label: MessageDescriptor }[];

export const DownloadOrganizationQuoteButton = () => {
  const { request } = useRequestContext();

  const { data: generatedDocuments = [] } = useRequestDocuments({
    requestId: request.id,
  });

  const organizationQuote = generatedDocuments.find(
    (document) => document.type === 'organization-quote'
  );

  if (!organizationQuote) {
    return null;
  }

  return (
    <Button
      variant="secondary"
      size="medium"
      href={organizationQuote.url}
      download
      style={{ alignSelf: 'start' }}
    >
      <Stack row alignItems="center" gap="0.5rem">
        <IconDownload style={{ fontSize: '1.25rem' }} />
        <Trans id="request.download-client-quote">Download the quote</Trans>
      </Stack>
    </Button>
  );
};
